import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import VendorDoc from 'src/models/AP/VendorDoc';
import { BiDownload } from 'react-icons/bi';
import { FaTrash, FaEdit } from 'react-icons/fa';

interface IVendorDocumentManagementDialogProps {
    dialogName: string;
    documentList?: Array<VendorDoc>;
    addDocument?: (description: string, file: File) => void;
    updateDocument?: (docId: number, description: string, file: File) => void;
    deleteDocument?: (docId: number) => void;
    downloadDocument?: (doc: VendorDoc) => void;
    hideDialog: () => void;
}

// Mirrors DepositDocumentManagementDialog.tsx's Manage Deposit Documents dialog (same table shape,
// same Add/Download/Delete pattern), plus an Update action - Update_Ap_Vendor_Document (oid 856)
// replaces the file on the same row rather than adding a new one, so this needs a per-row action
// the deposit-doc dialog doesn't have. Update also reuses the shared Description field and pushes
// it through to the same row - a deliberate departure from Oracle's own trigger (which only ever
// replaced the file, never DSCR), since leaving it un-editable meant no way to ever fix one.
const VendorDocumentManagementDialog = (props: IVendorDocumentManagementDialogProps) => {
    const { register, getValues, reset } = useForm();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [pendingUpdateDocId, setPendingUpdateDocId] = useState<number | null>(null);
    const updateFileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null);
    };

    const handleAddDocument = () => {
        if (!selectedFile) {
            return;
        }

        const description = getValues()?.documentDescription;
        props?.addDocument?.(description, selectedFile);
        setSelectedFile(null);
        reset({ documentDescription: '' });
    };

    const handleUpdateClick = (docId: number) => {
        setPendingUpdateDocId(docId);
        updateFileInputRef.current?.click();
    };

    const handleUpdateFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
        if (file && pendingUpdateDocId != null) {
            const description = getValues()?.documentDescription;
            props?.updateDocument?.(pendingUpdateDocId, description, file);
            reset({ documentDescription: '' });
        }
        e.target.value = '';
        setPendingUpdateDocId(null);
    };

    const handleClose = () => {
        props.hideDialog();
    };

    return (
        <Dialog title="Manage Vendor Documents" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '50%' }}>
            <input type="file" ref={updateFileInputRef} onChange={handleUpdateFileSelected} style={{ display: 'none' }} />
            <div style={{ display: 'flex', justifyContent: 'left', width: '100%' }}>
                <form style={{ width: '100%' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {/* .project-billing's own table CSS sets display:block, which breaks the
                            browser's normal table column-alignment so the header/cell width
                            percentages below aren't actually held to - long Description text bleeds
                            into the File Name column instead of wrapping/truncating within its own
                            cell. Overriding back to a real table with a fixed layout (inline, so it
                            doesn't touch every other .project-billing table in the app) restores it. */}
                        <table className={'project-billing'} style={{ display: 'table', tableLayout: 'fixed', width: '100%' }}>
                            <thead style={{ textAlign: 'center' }}>
                                <tr>
                                    <th style={{ width: '36%' }}>Description</th>
                                    <th style={{ width: '26%' }}>File Name</th>
                                    <th style={{ width: '9%' }}>Type</th>
                                    <th style={{ width: '16%' }}>Date</th>
                                    <th style={{ width: '13%' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {props?.documentList && props.documentList.length > 0 && props.documentList.map((doc: VendorDoc, index: number) => (
                                    <tr key={doc.id ?? index} className="table-item-hover" style={{ textAlign: 'center' }}>
                                        <td
                                            title={doc.description}
                                            style={{ width: '36%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {doc.description}
                                        </td>
                                        <td
                                            title={doc.originalFileName}
                                            style={{ width: '26%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {doc.originalFileName}
                                        </td>
                                        <td style={{ width: '9%' }}>{doc.documentType}</td>
                                        <td style={{ width: '16%' }}>{doc?.fileDate ? new Date(doc.fileDate).toLocaleDateString() : ''}</td>
                                        <td style={{ width: '13%' }}>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '.25rem', marginBottom: '1rem' }}>
                                                <Button
                                                    style={{ height: '2.2rem', background: 'transparent' }}
                                                    text={''}
                                                    title="Download"
                                                    reactIcon={BiDownload}
                                                    iconHoverColor="blue"
                                                    iconStyle={{ fill: 'rgb(99, 147, 193)', width: '1.6rem', height: '1.6rem', verticalAlign: 'center' }}
                                                    onClick={() => props?.downloadDocument(doc)}
                                                />
                                                <Button
                                                    style={{ height: '2.2rem', background: 'transparent' }}
                                                    text={''}
                                                    title="Update (replace file)"
                                                    reactIcon={FaEdit}
                                                    iconHoverColor="blue"
                                                    iconStyle={{ fill: 'rgb(99, 147, 193)', width: '1.6rem', height: '1.6rem', verticalAlign: 'center' }}
                                                    onClick={() => handleUpdateClick(doc.id)}
                                                />
                                                <Button
                                                    style={{ height: '2.2rem', background: 'transparent' }}
                                                    text={''}
                                                    title="Delete"
                                                    reactIcon={FaTrash}
                                                    iconHoverColor="blue"
                                                    iconStyle={{ fill: 'rgb(237, 53, 36)', width: '1.6rem', height: '1.6rem', verticalAlign: 'center' }}
                                                    onClick={() => props?.deleteDocument(doc.id)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {(!props?.documentList || props.documentList.length === 0) && (
                                    <tr style={{ height: '2rem' }}>
                                        <td colSpan={5} style={{ textAlign: 'center' }}>No documents</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: '1rem' }}>
                            <RHFInputBox
                                labelOnLeft={true}
                                label={'Description'}
                                name={'documentDescription'}
                                register={register}
                                style={{ width: '100%' }}
                                bodyStyle={{ width: '50%' }}
                            />
                            <input type="file" onChange={handleFileChange} style={{ marginLeft: '1rem' }} />
                            <Button
                                className="blue-hover-button"
                                style={{ width: '15%', marginLeft: '1rem' }}
                                text={'Add Document'}
                                disabled={!selectedFile}
                                onClick={() => handleAddDocument()}
                            />
                        </div>
                    </div>
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Close'}
                    onClick={() => handleClose()}
                />
            </div>
        </Dialog>
    );
};

export default VendorDocumentManagementDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/VendorDocumentManagementDialog.tsx